
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAbsoluteRoot, useGetRawParamsFromPath } from "./urlRoot";
import { useUrlFriendlyToParameters } from "./urlFriendly";
import { getPhaseRSC } from "./rsc";

export function useGetLanguages() {
    let query = ['CC_Languages']
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetConfigSports(params) {
    const { urlRoot } = useAbsoluteRoot();
    const { mirs_competition, mirs_discipline } = params
    let query = ['ConfigSports', mirs_competition, mirs_discipline, urlRoot];
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetConfigCompetitions() {
    const { urlRoot } = useAbsoluteRoot();
    let query = ['ConfigCompetitions', urlRoot];
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetCompetitions(params) {
    const { mirs_language } = params
    let query = ['CC_Competitions', mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetDisciplines(params) {
    const { mirs_competition, mirs_language } = params
    let query = ['CC_Disciplines', mirs_competition, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetFunctionDisciplines(params) {
    const queryClient = useQueryClient();
    return function (params) {
        const { mirs_competition, mirs_language } = params
        let query = ['CC_Disciplines', mirs_competition, mirs_language?.toUpperCase()]
        return queryClient.getQueryData(query);
    }
}

export function useGetH1(params) {
    const { mirs_competition } = params
    let query = ['CC_H1', mirs_competition]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetEvents(params) {
    const { mirs_competition, mirs_discipline, mirs_language } = params
    let query = ['CC_Events', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetFunctionEvents() {
    const queryClient = useQueryClient();
    return function (params) {
        const { mirs_competition, mirs_discipline, mirs_language } = params
        let query = ['CC_Events', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
        return queryClient.getQueryData(query);
    }
}

export function useGetEventUnits(params) {
    const { mirs_competition, mirs_discipline, mirs_language } = params
    let query = ['CC_EventUnits', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetFunctionEventUnits() {
    const queryClient = useQueryClient();
    return function (params) {
        const { mirs_competition, mirs_discipline, mirs_language } = params
        let query = ['CC_EventUnits', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
        return queryClient.getQueryData(query);
    }
}


export function useGetPositions(params) {
    const { mirs_competition, mirs_discipline, mirs_language } = params
    let query = ['CC_Positions', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

/**
 * 
 * @param {*} mlanguage mandatory param, 3 chars language code 
 * @returns 
 */
export function useGetRoutes(mlanguage) {
    let { mirs_competition } = useGetRawParamsFromPath()
    const dataCompetitions = useGetConfigCompetitions()
    let mcompetition = dataCompetitions?.competitions?.find(item => item.path === mirs_competition)?.competitionCode || mirs_competition

    const queryClientRouter = useQueryClient();
    const { urlRoot } = useAbsoluteRoot();
    let router_language = mlanguage?.toUpperCase();
    let queryRouter = ['routerPaths', { router_language, urlRoot: mcompetition ?  '/' + mcompetition + '/' : urlRoot  }];

    return queryClientRouter.getQueryData(queryRouter);
}

/**
 * 
 * @returns a function with mlanguage as mandatory param, 3 chars language code
 */
export function useGetFunctionRoutes() {
    const queryClientRouter = useQueryClient();
    const { urlRoot } = useAbsoluteRoot();
    let { mirs_competition } = useGetRawParamsFromPath()
    const dataCompetitions = useGetConfigCompetitions()
    let mcompetition = dataCompetitions?.competitions?.find(item => item.path === mirs_competition)?.competitionCode || mirs_competition

    return function (mlanguage) {
        let router_language = mlanguage;
        let queryRouter = ['routerPaths', { router_language, urlRoot:mcompetition ?  '/' + mcompetition + '/' : urlRoot }];
    return queryClientRouter.getQueryData(queryRouter);

    }
}

/**
 * 
 * @returns Return a OCS urls data , to use in high level components 
 */

export function useGetOCSDataURLs() {
    const { mirs_language } = useParams()
    const ccLanguages = useGetLanguages()
    const { urlRoot } = useAbsoluteRoot();
    const queryClientRouter = useQueryClient();
    const {mirs_competition} = useUrlFriendlyToParameters()

    if (!mirs_language) { return (null) }

    let router_language = mirs_language;

    if (router_language.length > 2) {
        router_language = ccLanguages?.languages?.find(item => item.lang?.toUpperCase() === mirs_language?.toUpperCase())?.langIsoCode;
    }

    if (!router_language) { return (null) }

    router_language = router_language.toLowerCase();

    let queryRouter = ['urlPaths', { router_language, urlRoot, mirs_competition }]
    return queryClientRouter.getQueryData(queryRouter);
}

export function useGetNocs(params) {
    const { mirs_competition, mirs_language } = params
    let query = ['CC_Nocs', mirs_competition, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetFunctionNocs() {
    const queryClient = useQueryClient();
    return function (params) {
        const { mirs_competition, mirs_language } = params
        let query = ['CC_Nocs', mirs_competition, mirs_language?.toUpperCase()]
        return queryClient.getQueryData(query);
    }
}

export function useGetScheduleStartList() {
    const { mirs_competition, mirs_language, mirs_discipline } = useUrlFriendlyToParameters()
    let query = ['getScheduleStartList', mirs_competition, mirs_discipline, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetSportCodes() {
    const { mirs_competition, mirs_language } = useUrlFriendlyToParameters()
    let query = ['SportCodes', mirs_competition, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetParticipantNames(params) {
    const { mirs_competition, mirs_language } = params
    let query = ['ParticipantNames', mirs_competition, mirs_language?.toUpperCase()]
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}

export function useGetFunctionParticipantNames() {
    const queryClient = useQueryClient();
    return function (params) {
        const { mirs_competition, mirs_language } = params
        let query = ['ParticipantNames', mirs_competition, mirs_language?.toUpperCase()]
        return queryClient.getQueryData(query);
    }
}

export function useGetUnitsDiscipline() {
    const { mirs_competition, mirs_language, mirs_rsc } = useUrlFriendlyToParameters()
    const _phase = getPhaseRSC(mirs_rsc)
    let query = ['unitsPhase', mirs_competition, mirs_language, _phase, 'Y']
    const queryClient = useQueryClient();
    return queryClient.getQueryData(query);
}


